import React, { useState, useEffect, useRef } from 'react';
import './room4.css';
import './room3.css';
import { useNavigate } from 'react-router-dom';

import Settings from '../components/common/settings';
import Lost from '../components/common/lost';
import Won from '../components/common/won';

import ClockPuzzle from './ClockPuzzle';
import SafePuzzle from './SafePuzzle';
import ToolboxPuzzle from './ToolboxPuzzle';

import lockedSound from '../audios/mixkit-select-click-1109.wav';
import settingsSound from '../audios/settoings.wav';

import roomBg from '../assets/room4.png';

const TOTAL_TIME = 10 * 60;
const ROOM_KEY = 'room4_time';

const Room4 = () => {
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(() => {
    localStorage.setItem(ROOM_KEY, Date.now());
    return TOTAL_TIME;
  });

  const [openSettings, setOpenSettings] = useState(false);
  const [showLosePopup, setShowLosePopup] = useState(false);
  const [showWin, setShowWin] = useState(false);

  const [showClock, setShowClock] = useState(false);
  const [showSafe, setShowSafe] = useState(false);
  const [showToolbox, setShowToolbox] = useState(false);

  const [clockDone, setClockDone] = useState(false);
  const [safeDone, setSafeDone] = useState(false);
  const [toolboxDone, setToolboxDone] = useState(false);

  const [candleFlicker, setCandleFlicker] = useState(false);

  const lockedAudio = useRef(null);
  const settingsAudio = useRef(null);

  useEffect(() => {
    lockedAudio.current = new Audio(lockedSound);
    settingsAudio.current = new Audio(settingsSound);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem(ROOM_KEY);
      if (!saved) return;
      const elapsed = Math.floor((Date.now() - Number(saved)) / 1000);
      const remaining = Math.max(TOTAL_TIME - elapsed, 0);
      setTimeLeft(remaining);
      if (remaining === 0) {
        setShowLosePopup(true);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCandleFlicker(true);
      setTimeout(() => setCandleFlicker(false), 600);
    }, 12000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (clockDone && safeDone && toolboxDone) {
      setTimeout(() => setShowWin(true), 800);
    }
  }, [clockDone, safeDone, toolboxDone]);

  const playLockedSound = () => {
    if (!lockedAudio.current) return;
    lockedAudio.current.currentTime = 0;
    lockedAudio.current.play().catch(() => {});
  };

  const playSettingsSound = () => {
    if (!settingsAudio.current) return;
    settingsAudio.current.currentTime = 0;
    settingsAudio.current.play().catch(() => {});
  };

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  const isWarning = timeLeft <= 60;
  const fillPercent = (timeLeft / TOTAL_TIME) * 100;

  return (
    <section
      className={`room4div ${candleFlicker ? 'room4flicker' : ''}`}
      style={{ backgroundImage: `url(${roomBg})` }}
    >
      <div className="room4vignette" />
      <div className="room4dust" />

      <div className="room1nav">
        <div />
        <div className="roomtimer">
          <span className="timerlabel">TIME REMAINING</span>
          <div className="timerdiv">
            <span className={`timerdigits ${isWarning ? 'timerwarning' : ''}`}>
              {minutes}:{seconds}
            </span>
          </div>
          <div className="timerbar">
            <div
              className="timerbartill"
              style={{
                width: `${fillPercent}%`,
                background: isWarning ? '#ff4444' : '#D4AF37',
              }}
            />
          </div>
        </div>
        <div className="settings" onClick={() => { playSettingsSound(); setOpenSettings(true); }} />
      </div>

      <div className="room3progress">
        <div className={`room3task ${clockDone ? 'taskdone' : ''}`}>
          {clockDone ? '✓' : '?'} GRANDFATHER CLOCK
        </div>
        <div className={`room3task ${safeDone ? 'taskdone' : ''}`}>
          {safeDone ? '✓' : '?'} SAFE
        </div>
        <div className={`room3task ${toolboxDone ? 'taskdone' : ''}`}>
          {toolboxDone ? '✓' : '?'} TOOLBOX
        </div>
      </div>

      <div
        className={`room4hotspot room4clockspot ${clockDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowClock(true); }}
        title="Examine the grandfather clock"
      >
        <span className='yy'>{clockDone ? '✓' : '👁'}</span>
      </div>

      <div
        className={`room4hotspot room4safespot ${safeDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowSafe(true); }}
        title="Examine the safe"
      >
        <span className='yy'>{safeDone ? '✓' : '👁'}</span>
      </div>

      <div
        className={`room4hotspot room4toolboxspot ${toolboxDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowToolbox(true); }}
        title="Examine the toolbox"
      >
        <span className='yy'>{toolboxDone ? '✓' : '👁'}</span>
      </div>

      {/* PUZZLE MODALS */}
      {showClock && (
        <ClockPuzzle
          onClose={() => setShowClock(false)}
          onSolve={() => { setClockDone(true); setShowClock(false); }}
        />
      )}

      {showSafe && (
        <SafePuzzle
          onClose={() => setShowSafe(false)}
          onSolve={() => { setSafeDone(true); setShowSafe(false); }}
        />
      )}

      {showToolbox && (
        <ToolboxPuzzle
          onClose={() => setShowToolbox(false)}
          onSolve={() => { setToolboxDone(true); setShowToolbox(false); }}
        />
      )}

      {/* Win screen */}
      {showWin && (
        <Won
          onReplay={() => {
            localStorage.removeItem(ROOM_KEY);
            window.location.reload();
          }}
          chooseRoomPath="/levels2"
        />
      )}

      <Settings
        open={openSettings}
        onClose={() => { playSettingsSound(); setOpenSettings(false); }}
        onPlaySound={playSettingsSound}
        onNavigate={navigate}
      />

      <Lost
        open={showLosePopup}
        onRetry={() => {
          localStorage.removeItem(ROOM_KEY);
          window.location.reload();
        }}
      />
    </section>
  );
};

export default Room4;